import {
    useState
} from "react";
import type {
    TraceRowHeightConstantType,
    TraceDurationConstantType,
    TraceCodeTextConstantType,
    TraceOpenConstantType,
    TraceJoinConstantType,
    TraceOpenType
} from "./type";
import {
    formatNumber
} from "../../helpers/locale";

const SECONDS_PER_MINUTE = 60;

const FRACTION_LIMIT_SECONDS = 10;

const TRACE_CODE_INDENT = 4;

export const getTraceDurationText = ({
    localize,
    seconds,
    locale
}: TraceDurationConstantType): string => {
    if(seconds < SECONDS_PER_MINUTE) {
        return localize("duration-seconds", [
            formatNumber({
                options: {
                    maximumFractionDigits: seconds < FRACTION_LIMIT_SECONDS ? 1 : 0
                },
                value: seconds,
                locale
            })
        ]);
    }

    const roundedSeconds = Math.round(seconds);

    return localize("duration-minutes-seconds", [
        formatNumber({
            value: Math.floor(roundedSeconds / SECONDS_PER_MINUTE),
            locale
        }),
        formatNumber({
            value: roundedSeconds % SECONDS_PER_MINUTE,
            locale
        })
    ]);
};

export const getTraceRowHeight = ({
    controlHeights,
    isInteractive,
    touchTargets,
    pointerType
}: TraceRowHeightConstantType): number => {
    if(isInteractive && pointerType === "coarse") {
        return touchTargets.preferred;
    }

    return controlHeights.small;
};

export const joinTraceParts = ({
    parts
}: TraceJoinConstantType): string | undefined => {
    const filledParts = parts.filter((part) => !!part);

    return filledParts.length ? filledParts.join(" · ") : undefined;
};

export const useTraceOpen = ({
    isAutoOpenWhileRunning = false,
    isDefaultOpen = false,
    isOpen: isOpenProp,
    onOpenChange,
    isRunning
}: TraceOpenConstantType): TraceOpenType => {
    const [
        userIsOpen,
        setUserIsOpen
    ] = useState<boolean | undefined>(undefined);

    const defaultIsOpen = isDefaultOpen || (isAutoOpenWhileRunning && isRunning);

    const isOpen = isOpenProp ?? userIsOpen ?? defaultIsOpen;

    const handleOpenChange = (nextIsOpen: boolean) => {
        setUserIsOpen(nextIsOpen);

        onOpenChange?.(nextIsOpen);
    };

    return {
        handleOpenChange,
        isOpen
    };
};

export const getTraceCodeText = ({
    value
}: TraceCodeTextConstantType): string => {
    if(typeof value === "string") {
        return value;
    }

    try {
        return JSON.stringify(value, null, TRACE_CODE_INDENT) ?? String(value);
    } catch {
        return String(value);
    }
};
